import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Box, 
  Breadcrumbs,
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Link as MuiLink,
  CircularProgress,
  Alert,
  Stack,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import Menu from '@mui/material/Menu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import Tooltip from '@mui/material/Tooltip';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { sectorCanCreateEvents } from '../../permissions';
import { RootState } from '../../store';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';
import { useBrand } from '../../brands/BrandProvider';
import { eventService } from '../../services/eventService';
import { parishService } from '../../services/parishService';
import api from '../../services/api';
import CreateEventViaAIChat from '../../features/ai-create-event/CreateEventViaAIChat';
import { AI_CREATE_EVENT_UI_ENABLED } from '../../features/ai-create-event/featureFlag';
import DuplicateEventDialog from './DuplicateEventDialog';

// Interface para evento formatado para exibição
interface DisplayEvent {
  id: string;
  slug: string;
  title: string;
  startDate: Date;
  endDate: Date;
  status: string;
  registeredCount: number;
  capacity: number;
  netRevenue: number;
  isFreeEvent: boolean;
  coverImage?: string;
  originParishId?: string;
  originParishName?: string;
  originIsSector?: boolean;
}

const formatNetRevenue = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

// Diálogo para trocar imagem de capa
const CoverImageDialog: React.FC<{
  event: DisplayEvent;
  onClose: () => void;
  onFileSelected: (file: File) => void;
  uploading: boolean;
}> = ({ event, onClose, onFileSelected, uploading }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
    disabled: uploading,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) onFileSelected(acceptedFiles[0]);
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trocar imagem de capa</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {event.title}
        </Typography>
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: uploading ? 'wait' : 'pointer',
            bgcolor: isDragActive ? 'action.hover' : 'action.hover',
            transition: 'all 0.2s',
            '&:hover': uploading ? {} : { borderColor: 'primary.main', bgcolor: 'action.selected' },
          }}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={40} />
              <Typography>Enviando imagem...</Typography>
            </Box>
          ) : (
            <>
              <AddPhotoAlternateIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                {isDragActive ? 'Solte a imagem aqui' : 'Arraste uma imagem ou clique para selecionar'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG, GIF ou WebP. Máx. 5MB.
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EventsListPage: React.FC = () => {
  const t = useBrandRegistrationCopy();
  const brand = useBrand();
  const eventNoun = brand.key === 'zuadaria' ? 'show' : 'evento';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const canCreateEvents = sectorCanCreateEvents(user);
  type ListTab = 'ativos' | 'rascunhos' | 'encerrados' | 'todos';

  const [events, setEvents] = useState<DisplayEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<DisplayEvent[]>([]);
  /** Visão da lista: ativos = publicados vigentes; rascunhos = pré/pausado/teste; encerrados = data fim passada. */
  const [listTab, setListTab] = useState<ListTab>('ativos');
  const [eventsByTab, setEventsByTab] = useState<Partial<Record<ListTab, DisplayEvent[]>>>({});
  const eventsByTabRef = useRef(eventsByTab);
  eventsByTabRef.current = eventsByTab;
  const listTabRef = useRef(listTab);
  listTabRef.current = listTab;
  const [tabCounts, setTabCounts] = useState({ ativos: 0, rascunhos: 0, encerrados: 0, todos: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [sectors, setSectors] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [eventForStatusChange, setEventForStatusChange] = useState<DisplayEvent | null>(null);
  const [statusChanging, setStatusChanging] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{open: boolean, event: DisplayEvent | null, newStatus: string | null}>({
    open: false, 
    event: null, 
    newStatus: null
  });
  const [coverDialogEvent, setCoverDialogEvent] = useState<DisplayEvent | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [createViaAiOpen, setCreateViaAiOpen] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<DisplayEvent | null>(null);

  const mapApiEvents = useCallback((rows: any[]): DisplayEvent[] =>
    (rows || []).map((event) => ({
      id: event._id,
      title: event.title,
      slug: event.slug,
      startDate: new Date(event.startDate),
      endDate: new Date(event.endDate),
      status: event.status,
      registeredCount: Number(event.registeredCount) || 0,
      capacity: event.capacity,
      netRevenue: Number(event.netRevenue) || 0,
      isFreeEvent: event.isFreeEvent === true || event.isPaid === false,
      coverImage: event.coverImage,
      originParishId: event.originParish?.id,
      originParishName: event.originParish?.name,
      originIsSector: event.originParish?.isSector === true,
    })),
  []);

  const fetchEvents = useCallback(
    async (opts?: { silent?: boolean; tab?: ListTab; force?: boolean }) => {
      const tab = opts?.tab || listTabRef.current;
      if (!opts?.force && eventsByTabRef.current[tab]) {
        setEvents(eventsByTabRef.current[tab] || []);
        setLoading(false);
        return;
      }
      try {
        if (!opts?.silent) {
          setLoading(true);
        }
        setError(null);
        const response = await eventService.getEvents({ listView: tab });
        if (response.success && response.data) {
          const formattedEvents = mapApiEvents(response.data);
          setEventsByTab((prev) => {
            const next = { ...prev, [tab]: formattedEvents };
            eventsByTabRef.current = next;
            return next;
          });
          if (tab === listTabRef.current) {
            setEvents(formattedEvents);
          }
          if (response.counts) {
            setTabCounts({
              ativos: Number(response.counts.ativos) || 0,
              rascunhos: Number(response.counts.rascunhos) || 0,
              encerrados: Number(response.counts.encerrados) || 0,
              todos: Number(response.counts.todos) || 0,
            });
          }
        } else {
          setError('Não foi possível carregar os eventos');
        }
      } catch (err) {
        console.error('Erro ao buscar eventos:', err);
        setError('Erro ao carregar eventos. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [mapApiEvents]
  );

  const invalidateEventsCache = useCallback(() => {
    eventsByTabRef.current = {};
    setEventsByTab({});
  }, []);

  useEffect(() => {
    void fetchEvents({ tab: listTab });
  }, [listTab, fetchEvents]);

  const isMainParish = user?.parish?.isMainParish === true;

  useEffect(() => {
    if (!isMainParish) {
      setSectors([]);
      return;
    }
    let cancelled = false;
    parishService
      .getMyParishes()
      .then((data) => {
        if (cancelled) return;
        setSectors((data.subParishes || []).map((sector) => ({ id: sector._id, name: sector.name })));
      })
      .catch(() => {
        if (!cancelled) setSectors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isMainParish]);

  const sectorChoices = useMemo(() => {
    const byId = new Map<string, string>();
    sectors.forEach((sector) => byId.set(sector.id, sector.name));
    events.forEach((event) => {
      if (event.originIsSector && event.originParishId) {
        byId.set(event.originParishId, event.originParishName || 'Setor');
      }
    });
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR')
    );
  }, [sectors, events]);

  const showSectors = isMainParish && sectorChoices.length > 0;

  const sectorLabel = (event: DisplayEvent) =>
    event.originIsSector ? event.originParishName || 'Setor' : 'Conta principal';

  // Busca/setor só no que já veio da aba atual (backend já filtrou a visão).
  useEffect(() => {
    let result = [...events];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(e => e.title.toLowerCase().includes(term));
    }
    if (showSectors && sectorFilter !== 'all') {
      result = result.filter((event) =>
        sectorFilter === 'main' ? !event.originIsSector : event.originParishId === sectorFilter
      );
    }
    setFilteredEvents(result);
  }, [events, searchTerm, showSectors, sectorFilter]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };


  // NOVA FUNÇÃO: Abrir menu de status clicando no chip
  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>, eventData: DisplayEvent) => {
    // Verificar se o evento está em pré-publicação
    if (eventData.status === 'pre_published') {
      setError('Não é possível alterar o status de eventos em pré-publicação. Aguarde a aprovação do administrador.');
      return;
    }
    
    setStatusMenuAnchor(event.currentTarget);
    setEventForStatusChange(eventData);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setEventForStatusChange(null);
  };

  const handleStatusChange = (newStatus: 'published' | 'paused' | 'test') => {
    if (!eventForStatusChange) return;
    
    // Mostrar diálogo de confirmação
    setConfirmDialog({
      open: true,
      event: eventForStatusChange,
      newStatus: newStatus
    });
    handleStatusMenuClose();
  };

  const confirmStatusChange = async () => {
    if (!confirmDialog.event || !confirmDialog.newStatus) return;

    try {
      setStatusChanging(confirmDialog.event.id);
      
      // Usar o serviço de API configurado que já inclui o token correto
      const response = await api.put(`/events/${confirmDialog.event.slug}/status`, {
        status: confirmDialog.newStatus
      });
      
      if (response.data.success) {
        invalidateEventsCache();
        await fetchEvents({ silent: true, force: true, tab: listTab });
        setSuccess(`✅ Status alterado para "${getStatusLabel(confirmDialog.newStatus!)}"`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('❌ Não foi possível alterar o status do evento');
      }
    } catch (err: any) {
      console.error('Erro ao alterar status do evento:', err);
      const errorMessage = err.response?.data?.message || 'Erro ao alterar status do evento. Por favor, tente novamente.';
      setError(`❌ ${errorMessage}`);
    } finally {
      setStatusChanging(null);
      setConfirmDialog({ open: false, event: null, newStatus: null });
    }
  };

  const cancelStatusChange = () => {
    setConfirmDialog({ open: false, event: null, newStatus: null });
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      'published': 'Publicado',
      'paused': 'Pausado',
      'test': 'Modo Teste',
      'pre_published': 'Pré-publicado'
    };
    return labels[status] || status;
  };

  // FUNÇÃO MELHORADA: Chip de status com indicador visual de clicável
  const getStatusChip = (status: string, isClickable: boolean = false, isLoading: boolean = false) => {
    const baseProps = {
      size: "small" as const,
      sx: isClickable ? {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
        opacity: isLoading ? 0.6 : 1,
        position: 'relative' as const
      } : {}
    };

     const chipContent = (() => {
       switch (status) {
         case 'published':
           return <Chip label="Publicado" color="success" {...baseProps} />;
         case 'paused':
           return <Chip label="Pausado" color="warning" {...baseProps} />;
         case 'test':
           return <Chip label="Modo Teste" color="secondary" {...baseProps} />;
         case 'pre_published':
           return <Chip label="Pré-publicado" color="info" {...baseProps} />;
         default:
           return <Chip label={status} {...baseProps} />;
       }
     })();

    if (isClickable && isLoading) {
      return (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          {chipContent}
          <CircularProgress 
            size={16} 
            sx={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              marginTop: '-8px', 
              marginLeft: '-8px' 
            }} 
          />
        </Box>
      );
    }

    return chipContent;
  };

  const formatDate = (date: Date) =>
    date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  const handleCoverFileSelected = useCallback(async (file: File) => {
    if (!coverDialogEvent) return;
    try {
      setCoverUploading(true);
      const response = await eventService.updateEventCover(coverDialogEvent.id, file);
      if (response.success && response.data) {
        const nextCover = response.data?.coverImage;
        setEvents((prev) =>
          prev.map((e) => (e.id === coverDialogEvent.id ? { ...e, coverImage: nextCover } : e))
        );
        setEventsByTab((prev) => {
          const current = prev[listTabRef.current];
          if (!current) return prev;
          const next = {
            ...prev,
            [listTabRef.current]: current.map((e) =>
              e.id === coverDialogEvent.id ? { ...e, coverImage: nextCover } : e
            ),
          };
          eventsByTabRef.current = next;
          return next;
        });
        setSuccess('Imagem de capa atualizada com sucesso!');
        setCoverDialogEvent(null);
      }
    } catch (err) {
      setError('Erro ao atualizar imagem. Tente novamente.');
    } finally {
      setCoverUploading(false);
    }
  }, [coverDialogEvent]);

  const EventThumbnail: React.FC<{ event: DisplayEvent }> = ({ event }) => (
    <Tooltip title="Clique para trocar a imagem" arrow placement="top">
      <Box
        onClick={() => setCoverDialogEvent(event)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'action.hover',
          border: '1px dashed',
          borderColor: 'divider',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.selected',
            '& .placeholder-icon': {
              color: 'primary.main',
            },
          },
        }}
      >
        {event.coverImage ? (
          <Box
            component="img"
            src={event.coverImage}
            alt={event.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            className="placeholder-icon"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <AddPhotoAlternateIcon sx={{ fontSize: 28 }} />
            <Typography variant="caption" sx={{ fontSize: 9, mt: 0.25 }}>
              Adicionar
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 6, md: 8 }, px: { xs: 2, sm: 3 } }}>
      <Breadcrumbs sx={{ mb: 1, fontSize: '0.78rem', '& .MuiBreadcrumbs-separator': { mx: 0.75 } }}>
        <MuiLink
          component={Link}
          to="/dashboard/eventos"
          underline="hover"
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.78rem' }}
        >
          Eventos
        </MuiLink>
        <Typography sx={{ fontWeight: 600, letterSpacing: 0.8, textTransform: 'uppercase', fontSize: '0.78rem', color: 'text.secondary' }}>
          Lista
        </Typography>
      </Breadcrumbs>
      {loading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 9999
        }}>
          <CircularProgress />
        </Box>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: { xs: 3, md: 4 } }}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Eventos
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
        >
          {canCreateEvents && AI_CREATE_EVENT_UI_ENABLED ? (
            <Button
              variant="outlined"
              startIcon={<SmartToyIcon />}
              onClick={() => setCreateViaAiOpen(true)}
              fullWidth={isMobile}
            >
              Criar via IA
            </Button>
          ) : null}
          {canCreateEvents && (
          <Button
            variant="contained"
            component={Link}
            to="/dashboard/eventos/novo"
            fullWidth={isMobile}
          >
            Criar Novo Evento
          </Button>
          )}
        </Stack>
      </Stack>

      {AI_CREATE_EVENT_UI_ENABLED ? (
        <CreateEventViaAIChat
          open={createViaAiOpen}
          onClose={() => {
            setCreateViaAiOpen(false);
            invalidateEventsCache();
            void fetchEvents({ silent: true, force: true });
          }}
        />
      ) : null}

      {coverDialogEvent && (
        <CoverImageDialog
          event={coverDialogEvent}
          onClose={() => setCoverDialogEvent(null)}
          onFileSelected={handleCoverFileSelected}
          uploading={coverUploading}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            animation: 'slideIn 0.3s ease-out',
            '@keyframes slideIn': {
              from: { transform: 'translateY(-20px)', opacity: 0 },
              to: { transform: 'translateY(0)', opacity: 1 }
            }
          }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, md: 4 } }}>
        <Tabs
          value={listTab}
          onChange={(_, value) => setListTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 2,
            minHeight: 44,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            },
          }}
        >
          <Tab value="ativos" label={`Ativos (${tabCounts.ativos})`} />
          <Tab value="rascunhos" label={`Rascunhos (${tabCounts.rascunhos})`} />
          <Tab value="encerrados" label={`Encerrados (${tabCounts.encerrados})`} />
          <Tab value="todos" label={`Todos (${tabCounts.todos})`} />
        </Tabs>

        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
          <Grid item xs={12} md={showSectors ? 5 : 8}>
            <TextField
              fullWidth
              size="small"
              label="Buscar eventos"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </Grid>
          {showSectors && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Setor</InputLabel>
                <Select<string>
                  value={sectorFilter}
                  label="Setor"
                  onChange={(e) => setSectorFilter(e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="main">Conta principal</MenuItem>
                  {sectorChoices.map((sector) => (
                    <MenuItem key={sector.id} value={sector.id}>{sector.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={showSectors ? 3 : 4}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              {filteredEvents.length} {eventNoun}(s) nesta aba
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {isMobile ? (
        <Stack spacing={2} sx={{ width: '100%', minWidth: 0 }}>
          {filteredEvents.length > 0 ? (
            filteredEvents.map((event) => (
              <Paper
                key={event.id}
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: 1,
                  },
                }}
                onClick={() => navigate(`/dashboard/eventos/${event.id}/workspace`)}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                    <EventThumbnail event={event} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                      {event.title}
                    </Typography>
                    {showSectors && (
                      <Chip
                        label={sectorLabel(event)}
                        size="small"
                        color={event.originIsSector ? 'primary' : 'default'}
                        variant={event.originIsSector ? 'filled' : 'outlined'}
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      {formatDate(event.startDate)}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <Tooltip
                        title={
                          event.status === 'pre_published'
                            ? 'Aguarde aprovação do administrador'
                            : 'Toque para alterar o status'
                        }
                        arrow
                      >
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusMenuOpen(e, event);
                          }}
                          sx={{
                            display: 'inline-block',
                            cursor: event.status === 'pre_published' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {getStatusChip(
                            event.status,
                            event.status !== 'pre_published',
                            statusChanging === event.id
                          )}
                        </Box>
                      </Tooltip>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('Inscrições:')} {event.registeredCount} / {event.capacity}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: event.isFreeEvent ? 'success.main' : 'text.primary',
                          }}
                        >
                          {event.isFreeEvent ? 'Gratuito' : formatNetRevenue(event.netRevenue)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title="Abrir painel do evento" arrow>
                    <IconButton
                      component={Link}
                      to={`/dashboard/eventos/${event.id}/workspace`}
                      size="small"
                      aria-label="Painel do evento"
                    >
                      <DashboardCustomizeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ver evento público" arrow>
                    <IconButton component={Link} to={`/${event.slug}`} size="small" aria-label="Ver público">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Duplicar ${eventNoun}`} arrow>
                    <IconButton
                      size="small"
                      aria-label={`Duplicar ${eventNoun}`}
                      onClick={() => setDuplicateSource(event)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar evento" arrow>
                    <IconButton
                      component={Link}
                      to={`/dashboard/eventos/${event.id}/editar`}
                      size="small"
                      aria-label="Editar"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ py: 1 }}>
                {loading ? 'Carregando eventos...' : 'Nenhum evento encontrado'}
              </Typography>
              {!loading && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 1 }}>
                  {canCreateEvents && AI_CREATE_EVENT_UI_ENABLED ? (
                    <Button variant="outlined" startIcon={<SmartToyIcon />} onClick={() => setCreateViaAiOpen(true)}>
                      Criar via IA
                    </Button>
                  ) : null}
                  {canCreateEvents && (
                  <Button variant="contained" component={Link} to="/dashboard/eventos/novo">
                    Criar Primeiro Evento
                  </Button>
                  )}
                </Stack>
              )}
            </Paper>
          )}
        </Stack>
      ) : (
      <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>Imagem</TableCell>
              <TableCell>Título</TableCell>
              {showSectors && <TableCell>Setor</TableCell>}
              <TableCell>Data de Início</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>{t('Inscrições')}</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEvents.length > 0 ? (
              filteredEvents.map(event => (
                <TableRow
                  key={event.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => navigate(`/dashboard/eventos/${event.id}/workspace`)}
                >
                  <TableCell sx={{ py: 1 }}>
                    <EventThumbnail event={event} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {event.title}
                      </Typography>
                      <Chip
                        label="Painel do evento"
                        size="small"
                        icon={<ArrowOutwardIcon />}
                        sx={{ fontWeight: 600, display: { xs: 'none', md: 'inline-flex' } }}
                      />
                    </Box>
                  </TableCell>
                  {showSectors && (
                    <TableCell>
                      <Chip
                        label={sectorLabel(event)}
                        size="small"
                        color={event.originIsSector ? 'primary' : 'default'}
                        variant={event.originIsSector ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  )}
                  <TableCell>{formatDate(event.startDate)}</TableCell>
                  {/* MELHORIA PRINCIPAL: Status clicável com tooltip */}
                  <TableCell>
                    <Tooltip 
                      title={event.status === 'pre_published' ? 'Aguarde aprovação do administrador' : 'Clique para alterar o status'} 
                      arrow
                      placement="top"
                    >
                      <Box 
                        onClick={(e) => handleStatusMenuOpen(e, event)} 
                        sx={{ 
                          display: 'inline-block',
                          cursor: event.status === 'pre_published' ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {getStatusChip(
                          event.status, 
                          event.status !== 'pre_published', // isClickable apenas se não for pre_published
                          statusChanging === event.id // isLoading
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {event.registeredCount} / {event.capacity}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 700,
                        color: event.isFreeEvent ? 'success.main' : 'success.dark',
                        lineHeight: 1.2,
                      }}
                    >
                      {event.isFreeEvent ? 'Gratuito' : formatNetRevenue(event.netRevenue)}
                    </Typography>
                  </TableCell>
                  {/* AÇÕES SIMPLIFICADAS: Removido o botão de alterar status */}
                  <TableCell align="right">
                    <Tooltip title="Abrir painel do evento" arrow>
                      <IconButton
                        component={Link}
                        to={`/dashboard/eventos/${event.id}/workspace`}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DashboardCustomizeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver evento público" arrow>
                      <IconButton
                        component={Link}
                        to={`/${event.slug}`} 
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={`Duplicar ${eventNoun}`} arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDuplicateSource(event);
                        }}
                        aria-label={`Duplicar ${eventNoun}`}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar evento" arrow>
                      <IconButton
                        component={Link}
                        to={`/dashboard/eventos/${event.id}/editar`}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={showSectors ? 7 : 6} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    {loading ? 'Carregando eventos...' : 'Nenhum evento encontrado'}
                  </Typography>
                  {!loading && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
                      {canCreateEvents && AI_CREATE_EVENT_UI_ENABLED ? (
                        <Button variant="outlined" startIcon={<SmartToyIcon />} onClick={() => setCreateViaAiOpen(true)}>
                          Criar via IA
                        </Button>
                      ) : null}
                      {canCreateEvents && (
                      <Button variant="contained" component={Link} to="/dashboard/eventos/novo">
                        Criar Primeiro Evento
                      </Button>
                      )}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Menu de alteração de status - MELHORADO com melhor posicionamento */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)',
            mt: 1
          }
        }}
      >
        <MenuItem 
          onClick={() => handleStatusChange('published')}
          sx={{ 
            color: '#2e7d32',
            '&:hover': { backgroundColor: '#e8f5e8' }
          }}
        >
          <CheckCircleIcon sx={{ mr: 1.5, fontSize: 20 }} />
          Publicado
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange('paused')}
          sx={{ 
            color: '#f57c00',
            '&:hover': { backgroundColor: '#fff3e0' }
          }}
        >
          <PauseIcon sx={{ mr: 1.5, fontSize: 20 }} />
          Pausado
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusChange('test')}
          sx={{ 
            color: '#9c27b0',
            '&:hover': { backgroundColor: '#f3e5f5' }
          }}
        >
          <PlayArrowIcon sx={{ mr: 1.5, fontSize: 20 }} />
          Modo Teste
        </MenuItem>
      </Menu>

      {/* Diálogo de confirmação de mudança de status - MELHORADO com melhor visual */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={cancelStatusChange}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: 'min(100%, 360px)', sm: 400 },
            m: { xs: 2, sm: 'auto' },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Alterar Status do Evento
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Tem certeza que deseja alterar o status do evento:
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            "{confirmDialog.event?.title}"
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              De:
            </Typography>
            {confirmDialog.event && getStatusChip(confirmDialog.event.status)}
            <Typography variant="body2" color="text.secondary">
              Para:
            </Typography>
            {confirmDialog.newStatus && getStatusChip(confirmDialog.newStatus)}
          </Box>
          {confirmDialog.newStatus === 'test' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              ℹ️ O evento ficará ativo para testes, mas não aparecerá na lista pública.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={cancelStatusChange}
            variant="outlined"
            disabled={statusChanging === confirmDialog.event?.id}
          >
            Cancelar
          </Button>
          <Button 
            onClick={confirmStatusChange}
            variant="contained"
            disabled={statusChanging === confirmDialog.event?.id}
            startIcon={statusChanging === confirmDialog.event?.id ? 
              <CircularProgress size={16} /> : 
              <CheckCircleIcon />
            }
            sx={{
              bgcolor: confirmDialog.newStatus === 'test' ? '#9c27b0' : '#2e7d32',
              '&:hover': {
                bgcolor: confirmDialog.newStatus === 'test' ? '#7b1fa2' : '#1b5e20'
              }
            }}
          >
            {statusChanging === confirmDialog.event?.id ? 'Alterando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DuplicateEventDialog
        open={!!duplicateSource}
        source={
          duplicateSource
            ? { id: duplicateSource.id, title: duplicateSource.title, slug: duplicateSource.slug }
            : null
        }
        onClose={() => setDuplicateSource(null)}
        onDuplicated={(newEventId) => {
          setDuplicateSource(null);
          setSuccess(
            eventNoun === 'show'
              ? '✅ Show duplicado. Abrindo edição…'
              : '✅ Evento duplicado. Abrindo edição…'
          );
          invalidateEventsCache();
          void fetchEvents({ silent: true, force: true });
          navigate(`/dashboard/eventos/${newEventId}/editar`);
        }}
      />

    </Container>
  );
};

export default EventsListPage;
